import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema, paginationQuerySchema } from './primitives';
/**
 * Lots de numérisation (cahier des charges §4 et §5).
 *
 * Un lot matérialise une session de numérisation dans un bureau donné. Il porte
 * la traçabilité de bout en bout : qui a numérisé, quand, combien de reçus, et
 * par quels états le travail est passé.
 */
export const LOT_STATUSES = {
    DRAFT: 'DRAFT',
    SCANNING: 'SCANNING',
    OCR_RUNNING: 'OCR_RUNNING',
    VALIDATION_REQUIRED: 'VALIDATION_REQUIRED',
    ARCHIVED: 'ARCHIVED',
    ERROR: 'ERROR',
};
export const lotStatusSchema = z.enum([
    LOT_STATUSES.DRAFT,
    LOT_STATUSES.SCANNING,
    LOT_STATUSES.OCR_RUNNING,
    LOT_STATUSES.VALIDATION_REQUIRED,
    LOT_STATUSES.ARCHIVED,
    LOT_STATUSES.ERROR,
]);
export const LOT_STATUS_LABELS = {
    DRAFT: 'En préparation',
    SCANNING: 'En cours de numérisation',
    OCR_RUNNING: 'OCR en cours',
    VALIDATION_REQUIRED: 'Validation requise',
    ARCHIVED: 'Archivé',
    ERROR: 'Erreur',
};
/**
 * Transitions autorisées de la machine à états d'un lot.
 * Toute transition absente de cette table est rejetée par le domaine.
 */
export const LOT_TRANSITIONS = {
    DRAFT: ['SCANNING', 'ERROR'],
    // `SCANNING → ARCHIVED` est admis : quand tous les documents d'un lot sont
    // déjà prêts, l'opérateur ne doit pas attendre qu'une tâche de fond ait
    // repositionné le statut pour pouvoir archiver. Les conditions de fond
    // (aucune exception ouverte, au moins un document) restent vérifiées par
    // l'agrégat, quelle que soit la transition empruntée.
    SCANNING: ['OCR_RUNNING', 'VALIDATION_REQUIRED', 'ARCHIVED', 'ERROR'],
    OCR_RUNNING: ['VALIDATION_REQUIRED', 'ARCHIVED', 'ERROR'],
    VALIDATION_REQUIRED: ['OCR_RUNNING', 'ARCHIVED', 'ERROR'],
    ARCHIVED: [],
    ERROR: ['DRAFT', 'SCANNING', 'OCR_RUNNING', 'VALIDATION_REQUIRED'],
};
export function canTransitionLot(from, to) {
    return LOT_TRANSITIONS[from].includes(to);
}
/** Origine des pages d'un lot — le port d'acquisition reste ouvert. */
export const INGESTION_SOURCES = {
    WEB_UPLOAD: 'WEB_UPLOAD',
    WATCH_FOLDER: 'WATCH_FOLDER',
    SCANNER_AGENT: 'SCANNER_AGENT',
};
export const ingestionSourceSchema = z.enum([
    INGESTION_SOURCES.WEB_UPLOAD,
    INGESTION_SOURCES.WATCH_FOLDER,
    INGESTION_SOURCES.SCANNER_AGENT,
]);
export const INGESTION_SOURCE_LABELS = {
    WEB_UPLOAD: 'Dépôt web',
    WATCH_FOLDER: 'Dossier surveillé',
    SCANNER_AGENT: 'Agent scanner',
};
export const lotSchema = z.object({
    id: idSchema,
    reference: z.string(),
    branchId: idSchema,
    branchCode: z.string(),
    branchName: z.string(),
    status: lotStatusSchema,
    source: ingestionSourceSchema,
    operatorUserId: idSchema,
    operatorName: z.string(),
    scanDate: isoDateSchema,
    notes: z.string().nullable(),
    receiptCount: z.number().int().nonnegative(),
    pageCount: z.number().int().nonnegative(),
    pendingReviewCount: z.number().int().nonnegative(),
    duplicateCount: z.number().int().nonnegative(),
    errorMessage: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    scanStartedAt: isoDateTimeSchema.nullable(),
    ocrStartedAt: isoDateTimeSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
});
/** Une ligne d'historique du lot, telle qu'affichée dans le suivi d'un lot. */
export const lotEventSchema = z.object({
    id: idSchema,
    occurredAt: isoDateTimeSchema,
    action: z.string(),
    label: z.string(),
    actorName: z.string().nullable(),
    fromStatus: lotStatusSchema.nullable(),
    toStatus: lotStatusSchema.nullable(),
    detail: z.string().nullable(),
});
export const lotDetailSchema = lotSchema.extend({
    events: z.array(lotEventSchema),
});
export const createLotSchema = z.object({
    /** Optionnel : un Branch Leader ne peut créer un lot que pour son bureau. */
    branchId: idSchema.optional(),
    scanDate: isoDateSchema.optional(),
    source: ingestionSourceSchema.default('WEB_UPLOAD'),
    notes: z.string().trim().max(500).optional(),
});
export const updateLotSchema = z.object({
    notes: z.string().trim().max(500).nullable().optional(),
    scanDate: isoDateSchema.optional(),
});
export const listLotsQuerySchema = paginationQuerySchema.extend({
    branchId: idSchema.optional(),
    status: lotStatusSchema.optional(),
    operatorUserId: idSchema.optional(),
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
    search: z.string().trim().max(120).optional(),
});
/** Résultat d'un dépôt de fichiers dans un lot. */
export const ingestionResultSchema = z.object({
    lotId: idSchema,
    acceptedFiles: z.number().int().nonnegative(),
    rejectedFiles: z.array(z.object({ fileName: z.string(), reason: z.string() })),
    queuedJobs: z.array(idSchema),
});
/** Suivi temps réel du traitement d'un lot (sondé par le poste de numérisation). */
export const lotProgressSchema = z.object({
    lotId: idSchema,
    status: lotStatusSchema,
    totalReceipts: z.number().int().nonnegative(),
    processedReceipts: z.number().int().nonnegative(),
    ocrPending: z.number().int().nonnegative(),
    needsReview: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    percentComplete: z.number().min(0).max(100),
});
//# sourceMappingURL=scanning.js.map